import React from 'react'
import moment from 'moment'
import { Checkbox } from '@material-ui/core'
import {
  CheckBoxOutlineBlank as TaskPendingIcon,
  Event as FollowUpIcon,
} from '@material-ui/icons'
import { Text } from '.'

// Today / Tomorrow / Yesterday + time, dropping the year when it's the current one — same formatter as the Lead
// list card (apps/leads/pages/LeadList/LeadCard.js), kept local per this codebase's per-file helper convention.
export const formatFollowUpTime = (value) => {
  const time = moment(value)
  const today = moment().startOf('day')
  const timeDay = time.clone().startOf('day')

  let dayLabel = ''
  if (timeDay.isSame(today)) dayLabel = 'Today'
  else if (timeDay.isSame(today.clone().add(1, 'day'))) dayLabel = 'Tomorrow'
  else if (timeDay.isSame(today.clone().subtract(1, 'day'))) dayLabel = 'Yesterday'

  if (dayLabel) return `${dayLabel}, ${time.format('HH:mm')}`

  const showYear = time.year() !== moment().year()
  return time.format(showYear ? 'DD MMM yyyy HH:mm' : 'DD MMM HH:mm')
}

// A "slight" gap between rows — CardBody and the board card render rows bare, so each row adds its own.
const rowStyle = { marginTop: 6 }

/**
 * The issue's next meeting and next due task as the rows the Lead list card shows:
 *  - calendar icon + "Today, 14:00 Meeting name" (+ " · N scheduled" when there are several)
 *  - empty-checkbox icon + task name (+ " · N overdue"), red when overdue / orange when due today
 * The issue list endpoint carries the pre-computed next_event_data / next_task_data; each row only exists when its data
 * does, so the result is [] for an issue with nothing to show. Overdue / due-today come from the task's due date
 * (overdue_task_count alone isn't reliable for that). `selected` flips to the lighter tints a colored card needs;
 * `show` (['event'] / ['task']) limits the rows — it comes from the scheme card field `{ component: 'NextActivity' }`.
 * `onCompleteTask` (the detail summary only; list cards stay read-only) swaps the task's icon for a checkbox that calls
 * it with the task; `completedTaskUid` keeps that task's box ticked while the completion is being saved / re-read.
 */
export const getNextActivityRows = (item, { selected = false, show, onCompleteTask, completedTaskUid } = {}) => {
  const nextEvent = (!show || show.includes('event')) ? item?.next_event_data : null
  const nextTask = (!show || show.includes('task')) ? item?.next_task_data : null
  const nextEventTime = nextEvent ? (item?.next_event_time || nextEvent?.starts_at) : null
  const nextTaskDueDate = nextTask ? (item?.next_task_due_date || nextTask?.due_at) : null
  const scheduledEventCount = item?.scheduled_event_count || 0
  const overdueTaskCount = item?.overdue_task_count || 0

  const iconColor = selected ? '#fff' : '#9e9e9e'
  const isTaskOverdue = Boolean(nextTaskDueDate) && moment(nextTaskDueDate).isBefore(moment(), 'day')
  const isTaskDueToday = !isTaskOverdue && Boolean(nextTaskDueDate) && moment(nextTaskDueDate).isSame(moment(), 'day')
  const taskColor = isTaskOverdue
    ? (selected ? '#ff5252' : '#d32f2f')
    : isTaskDueToday
      ? (selected ? '#ffcc80' : '#f57c00')
      : iconColor

  const rows = []
  if (nextEvent) {
    rows.push(
      <div key="next-event" style={{ display: 'flex', alignItems: 'center', gap: 4, ...rowStyle }}>
        <FollowUpIcon fontSize="small" style={{ color: iconColor, flexShrink: 0 }} />
        <Text variant="body2" component="span" noWrap={true} textCustomProps={{ fontWeight: 700 }}>
          {nextEventTime && formatFollowUpTime(nextEventTime)}
          {nextEventTime && nextEvent?.name ? ' ' : ''}
          {nextEvent?.name}
          {scheduledEventCount > 1 ? ` · ${scheduledEventCount} scheduled` : ''}
        </Text>
      </div>
    )
  }
  if (nextTask) {
    rows.push(
      <div key="next-task" style={{ display: 'flex', alignItems: 'center', gap: 4, ...rowStyle }}>
        {onCompleteTask ? (
          // padding 2 / margin -2: a 24px click target in the 20px icon's footprint, so the row doesn't grow
          <Checkbox
            checked={completedTaskUid != null && completedTaskUid === nextTask.uid}
            disabled={completedTaskUid != null && completedTaskUid === nextTask.uid}
            onChange={() => onCompleteTask(nextTask)}
            color="primary"
            size="small"
            style={{ padding: 2, margin: -2, flexShrink: 0 }}
          />
        ) : (
          <TaskPendingIcon fontSize="small" style={{ color: taskColor, flexShrink: 0 }} />
        )}
        <Text variant="body2" component="span" noWrap={true} textCustomProps={{ color: taskColor }}>
          {nextTask.name}
          {overdueTaskCount > 1 ? ` · ${overdueTaskCount} overdue` : ''}
        </Text>
      </div>
    )
  }
  return rows
}

/** scheme card field component (`{ "component": "NextActivity" }`); renders nothing when there's nothing to show */
const NextActivityRows = ({ item, show, selected, onCompleteTask, completedTaskUid }) => {
  const rows = getNextActivityRows(item, { show, selected, onCompleteTask, completedTaskUid })
  if (rows.length === 0) return null
  return <div style={{ minWidth: 0 }}>{rows}</div>
}

export default NextActivityRows
